import { useMutation } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useSetAppliedSnapshotForCandidacy } from "@/hooks/sets/useSetAppliedSnapshotForCandidacy";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import {
  Candidacy,
  CandidacyStatusChange,
  HttpResponse,
  MinimalCandidacyStatusChange,
} from "@/types";

interface UndoLastCandidacyStatusChangeResponse {
  candidacy: Candidacy;
  candidacyStatusChange: CandidacyStatusChange | null;
}

export function useUpdateSetUndoLastCandidacyStatusChange() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const setAppliedSnapshotForCandidacy = useSetAppliedSnapshotForCandidacy();
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();

  return useMutation({
    mutationFn: async ({ candidacyId }: { candidacyId: string }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}/undo-last-status-change`;
      const res = await guacHttpClient.post<
        UndoLastCandidacyStatusChangeResponse,
        HttpResponse<UndoLastCandidacyStatusChangeResponse>
      >(path);
      return res.data;
    },
    onSuccess: ({ candidacy, candidacyStatusChange }) => {
      if (!candidacyStatusChange) return;

      const restoredLastChange: MinimalCandidacyStatusChange = {
        createdAt:
          typeof candidacyStatusChange.createdAt === "string"
            ? candidacyStatusChange.createdAt
            : new Date(candidacyStatusChange.createdAt).toISOString(),
        fromStatus: candidacyStatusChange.fromStatus,
        toStatus: candidacyStatusChange.toStatus,
        statusChangeReason: candidacyStatusChange.statusChangeReason,
        byAgent: candidacyStatusChange.byAgent,
      };

      // The snapshot has to move before the lists do — it counts the applied
      // rows still sitting in the cache the invalidate below is about to drop.
      setAppliedSnapshotForCandidacy({
        candidacyId: candidacy.id,
        fromStatus: restoredLastChange.fromStatus ?? undefined,
        toStatus: restoredLastChange.toStatus,
      });
      setCandidacyInAllLists({
        candidacyId: candidacy.id,
        lastCandidacyStatusChange: restoredLastChange,
      });
      setCandidacyInfo({
        candidacyId: candidacy.id,
        status: restoredLastChange.toStatus,
      });
    },
  });
}
